/* eslint-disable @typescript-eslint/no-explicit-any */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axiosInstance from "@/lib/axios";
import { courseEndpoints } from "@/config/endpoints";
import { useAppSelector } from "@/stores/hook";

const PAGE_SIZE = 20;

/**
 * Infinite-paginated courses list, filtered by the redux `courseFilter`
 * slice. Mirrors `useUniversities` so the grids share the same pattern.
 */
export default function useCourses() {
  const appliedFilters = useAppSelector(
    (state) => state.courseFilter.appliedFilters
  );

  const query = useInfiniteQuery({
    queryKey: ["courses", "infinite", appliedFilters],
    queryFn: async ({ pageParam = 1 }) => {
      const params = {
        ...appliedFilters,
        page: pageParam,
        limit: PAGE_SIZE,
      };
      const res = await axiosInstance.get(courseEndpoints, { params });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) => {
      const total = Number(lastPage?.meta?.total) || 0;
      const loaded = allPages.flatMap(
        (p: any) => p.courses || p.data || []
      ).length;
      if (loaded > 0 && loaded < total) return allPages.length + 1;
      const lastBatch = (lastPage?.courses || lastPage?.data || []).length;
      if (!total && lastBatch === PAGE_SIZE) return allPages.length + 1;
      return undefined;
    },
    select: (data) => {
      const courses = data.pages.flatMap(
        (p: any) => p.courses || p.data || []
      );
      return {
        courses,
        totalCount: Number(data.pages[0]?.meta?.total) || courses.length,
      };
    },
  });

  const courses = query.data?.courses ?? [];
  const totalCount = query.data?.totalCount ?? 0;

  const courseSelects = courses.map((val: any) => ({
    value: String(val.id),
    name: val.name,
  }));

  return {
    courses,
    courseSelects,
    length: totalCount,
    loadedCount: courses.length,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

/**
 * Unpaginated full list (used by surfaces like the AttachCourse modal that
 * need every course up-front for client-side search / filtering).
 *
 * Walks pages until the server stops returning a full batch, so it stays
 * correct as the catalog grows past any single-request cap (the previous
 * hardcoded `limit: 1000` silently truncated tenants with >1000 courses —
 * Skymark hit ~2000 and was missing half the catalog). Result is cached for
 * 5 min, so the extra round-trips happen once per session per filter set.
 */
// `enabled` lets callers defer the full-catalog walk until it's actually
// needed (e.g. only when the Attach-Course modal is OPEN) instead of running
// it inside a permanently-mounted hidden dialog. Defaults to true so existing
// callers are unchanged. The full client-side list/search is untouched — when
// enabled it still fetches every course; the shared cache key + 5min staleTime
// mean the first open warms it and reopens are instant.
export function useCourseOptions({
  enabled = true,
  /**
   * Extra server-side filters (e.g. `universityId=3,7` and/or
   * `cf_<fieldId>=12` from the Study Preferences cascade — the server
   * matches CF values on the course OR its university). Part of the query
   * key, so each filter combination caches independently.
   */
  params,
}: { enabled?: boolean; params?: Record<string, string> } = {}) {
  const query = useQuery({
    queryKey: ["courses", "options", params ?? {}],
    queryFn: async () => {
      const PAGE = 1000;
      const MAX_PAGES = 50; // safety net: 50k courses
      const all: any[] = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const res = await axiosInstance.get(courseEndpoints, {
          params: { limit: PAGE, page, ...(params ?? {}) },
        });
        const batch: any[] = res.data?.courses ?? res.data?.data ?? [];
        all.push(...batch);
        const total = Number(res.data?.meta?.total) || 0;
        if (total > 0 && all.length >= total) break;
        if (batch.length < PAGE) break;
      }
      return all;
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  return {
    courses: query.data ?? [],
    isLoading: query.isLoading,
  };
}

export function useInfiniteCourses(
  filters: Record<string, any> = {},
  pageSize = PAGE_SIZE
) {
  const query = useInfiniteQuery({
    queryKey: ["courses", "infinite-local", filters, pageSize],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await axiosInstance.get(courseEndpoints, {
        params: {
          ...filters,
          page: pageParam,
          limit: pageSize,
        },
      });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any, allPages) => {
      const total = Number(lastPage?.meta?.total) || 0;
      const loaded = allPages.flatMap(
        (p: any) => p.courses || p.data || []
      ).length;
      if (loaded > 0 && loaded < total) return allPages.length + 1;
      const lastBatch = (lastPage?.courses || lastPage?.data || []).length;
      if (!total && lastBatch === pageSize) return allPages.length + 1;
      return undefined;
    },
    select: (data) => {
      const courses = data.pages.flatMap(
        (p: any) => p.courses || p.data || []
      );
      return {
        courses,
        totalCount: Number(data.pages[0]?.meta?.total) || courses.length,
      };
    },
  });

  return {
    courses: query.data?.courses ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}
